import { clsx } from 'clsx';
import { isNil } from 'lodash-es';

import styles from '../../styles/element.module.scss';

export const paddingSizes = [
  'base',
  '128',
  '7xl', // 128px
  '96',
  '6xl', // 96px
  '80',
  '5xl', // 80px
  '64',
  '4xl', // 64px
  '48',
  '3xl', // 48px
  '40',
  '2xl', // 40px
  '32',
  'xl', // 32px
  '24',
  'lg', // 24px
  '20',
  'md', // 20px
  '16',
  'sm', // 16px
  '12',
  'xs', // 12px
  '8',
  '2xs', // 8px
  '4',
  '3xs', // 4px
  '2',
  '4xs', // 2px
  'none',
  'zero' // 0px
] as const;

export type PaddingSizes = (typeof paddingSizes)[number];

export const marginSizes = ['auto', ...paddingSizes] as const;

export type MarginSizes = (typeof marginSizes)[number];

const sizeMapping = {
  '2': '4xs',
  '4': '3xs',
  '8': '2xs',
  '12': 'xs',
  '16': 'sm',
  '20': 'md',
  '24': 'lg',
  '32': 'xl',
  '40': '2xl',
  '48': '3xl',
  '64': '4xl',
  '80': '5xl',
  '96': '6xl',
  '128': '7xl',
  zero: 'zero',
  base: 'base'
} as const;

export const mapSize = (size: PaddingSizes | MarginSizes) =>
  size in sizeMapping ? sizeMapping[size as keyof typeof sizeMapping] : size;

// the padding to apply to all sides of an html element
interface PaddingProps {
  padding?: PaddingSizes;
  paddingTop?: PaddingSizes;
  paddingRight?: PaddingSizes;
  paddingBottom?: PaddingSizes;
  paddingLeft?: PaddingSizes;
  paddingH?: PaddingSizes;
  paddingV?: PaddingSizes;
}

interface MarginProps {
  // the margin size to apply to all sides of an html element
  margin?: MarginSizes;
  marginTop?: MarginSizes;
  marginRight?: MarginSizes;
  marginBottom?: MarginSizes;
  marginLeft?: MarginSizes;
  marginH?: PaddingSizes;
  marginV?: PaddingSizes;
}

export type SpacingProps = PaddingProps & MarginProps;

export const spacingClassNames = (
  styles: Record<string, string>,
  propKey: string,
  propValue: PaddingSizes | MarginSizes | undefined
): Record<string, boolean> => {
  const classNames: Record<string, boolean> = {};

  if (!isNil(propValue)) {
    const mappedValue = mapSize(propValue);

    classNames[styles[`${propKey}-${mappedValue}`] as keyof typeof classNames] =
      true;
  }

  return classNames;
};

export const spacingHelpers = {
  defaultProps: {
    padding: undefined,
    margin: undefined
  },
  classnames: (props: SpacingProps): string => {
    return clsx({
      ...spacingClassNames(styles, 'p', props.padding),
      ...spacingClassNames(styles, 'pt', props.paddingTop),
      ...spacingClassNames(styles, 'pr', props.paddingRight),
      ...spacingClassNames(styles, 'pb', props.paddingBottom),
      ...spacingClassNames(styles, 'pl', props.paddingLeft),
      ...spacingClassNames(styles, 'ph', props.paddingH),
      ...spacingClassNames(styles, 'pv', props.paddingV),
      ...spacingClassNames(styles, 'm', props.margin),
      ...spacingClassNames(styles, 'mt', props.marginTop),
      ...spacingClassNames(styles, 'mr', props.marginRight),
      ...spacingClassNames(styles, 'mb', props.marginBottom),
      ...spacingClassNames(styles, 'ml', props.marginLeft),
      ...spacingClassNames(styles, 'mh', props.marginH),
      ...spacingClassNames(styles, 'mv', props.marginV)
    });
  },
  base: ({
    padding,
    paddingTop,
    paddingRight,
    paddingLeft,
    paddingBottom,
    paddingH,
    paddingV,
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    marginH,
    marginV,
    ...props
  }: SpacingProps & unknown) => props
};
